import {
	appendRuleHoverDataForNode as popupAppendRuleHoverDataForNode,
	buildLiveEditorMatchHoverText as popupBuildLiveEditorMatchHoverText,
	buildRuleSnippetForHover as popupBuildRuleSnippetForHover,
	ensureLiveEditorHoverPopupBinding as popupEnsureLiveEditorHoverPopupBinding,
	ensureLiveEditorHoverPopupNode as popupEnsureLiveEditorHoverPopupNode,
	hideLiveEditorHoverPopup as popupHideLiveEditorHoverPopup,
	resolveBlockTypeFromNode as popupResolveBlockTypeFromNode,
	setLiveEditorMatchHoverData as popupSetLiveEditorMatchHoverData,
	showLiveEditorHoverPopup as popupShowLiveEditorHoverPopup,
	teardownLiveEditorHoverPopupBinding as popupTeardownLiveEditorHoverPopupBinding
} from './rules-view-highlight-popup';
import {
	buildEditorCanvasBlocksMap as matchingBuildEditorCanvasBlocksMap,
	canonicalizeBlockType as matchingCanonicalizeBlockType,
	clearLiveToEditorMatchHighlights as matchingClearLiveToEditorMatchHighlights,
	flattenBlocksByType as matchingFlattenBlocksByType,
	generateA11yFixUuid as matchingGenerateA11yFixUuid,
	getLiveEditorMatchSelectorForNode as matchingGetLiveEditorMatchSelectorForNode,
	mapLiveBlocksToEditorAndHighlight as matchingMapLiveBlocksToEditorAndHighlight,
	resolveBlockMatchThreshold as matchingResolveBlockMatchThreshold,
	resolveFirstPostContentIndex as matchingResolveFirstPostContentIndex,
	scoreLiveToEditorBlockMatch as matchingScoreLiveToEditorBlockMatch,
	setLiveToEditorMatchStyle as matchingSetLiveToEditorMatchStyle,
	shouldHighlightMappedEditorBlock as matchingShouldHighlightMappedEditorBlock,
	shouldIncludeLiveBlockForEditorMapping as matchingShouldIncludeLiveBlockForEditorMapping
} from './rules-view-highlight-matching';
import {
	extractSelectorTokens as fallbackExtractSelectorTokens,
	findFallbackFormControlNodes as fallbackFindFallbackFormControlNodes,
	findFallbackHeadingNodes as fallbackFindFallbackHeadingNodes,
	findFallbackImageNodes as fallbackFindFallbackImageNodes,
	findFallbackKeyboardNodes as fallbackFindFallbackKeyboardNodes,
	findFallbackLinkNodes as fallbackFindFallbackLinkNodes,
	findFallbackLowContrastNodes as fallbackFindFallbackLowContrastNodes,
	findFallbackNodesForRule as fallbackFindFallbackNodesForRule,
	findNodesBySelectorTail as fallbackFindNodesBySelectorTail,
	getLowContrastIssueItems as fallbackGetLowContrastIssueItems,
	getRuleFallbackDiagnostics as fallbackGetRuleFallbackDiagnostics,
	hasStrongTokenOverlap as fallbackHasStrongTokenOverlap,
	matchesSelectorTokenHints as fallbackMatchesSelectorTokenHints
} from './rules-view-highlight-fallbacks';
import {
	buildRulesViewWpBlocksMap as inspectionBuildRulesViewWpBlocksMap,
	classifyBlockRegion as inspectionClassifyBlockRegion,
	collectBlockRegionContainers as inspectionCollectBlockRegionContainers,
	collectEditorCanvasDocuments as inspectionCollectEditorCanvasDocuments,
	collectFailedRuleKeysByBlockNode as inspectionCollectFailedRuleKeysByBlockNode,
	createBlockRegionContext as inspectionCreateBlockRegionContext,
	describeTargetDocument as inspectionDescribeTargetDocument,
	getBlockRegionFooterSelectors as inspectionGetBlockRegionFooterSelectors,
	getBlockRegionHeaderSelectors as inspectionGetBlockRegionHeaderSelectors,
	getHighlightTargetDocuments as inspectionGetHighlightTargetDocuments,
	hasEditorCanvasIframe as inspectionHasEditorCanvasIframe,
	hasNodeAncestorToken as inspectionHasNodeAncestorToken,
	isNodeWithinRegionContainers as inspectionIsNodeWithinRegionContainers
} from './rules-view-highlight-inspection';

export class RulesViewHighlightController {
	constructor(ruleToggleButtons, getRulesPreviewIframe, i18n = null) {
		this.ruleToggleButtons = ruleToggleButtons;
		this.getRulesPreviewIframe = getRulesPreviewIframe;
		this.__ = typeof i18n === 'function' ? i18n : null;
		this.rulesViewSelectorsByRule = {};
		this.rulesViewRuleMetadataByKey = {};
		this.baseRuleHasHighlightCandidatesByKey = {};
		this.ruleHasHighlightCandidatesByKey = {};
		this.activeRuleHighlightKeys = new Set();
		this.areRulesHighlightsVisible = false;
		this.editorRehighlightTimerId = 0;
		this.editorRehighlightAttempts = 0;
		this.liveEditorPopupBindings = new Map();
		this.rulesViewMapHistory = [];
	}

	getVirtualToggleRuleMap() {
		return {
			ariaAndNames: ['accessibleNamePreferredSource', 'ariaLabelledbyIntegrity', 'ariaHiddenInteractive']
		};
	}

	resolveToggleRuleKeys(ruleKey) {
		const key = String(ruleKey || '').trim();
		if (!key) {
			return [];
		}

		const mappedKeys = this.getVirtualToggleRuleMap()?.[key];
		if (!Array.isArray(mappedKeys) || mappedKeys.length === 0) {
			return [key];
		}

		return mappedKeys.filter((mappedKey) => typeof mappedKey === 'string' && mappedKey.length > 0);
	}


	normalizeRuleKeys(ruleKeys) {
		return Array.isArray(ruleKeys)
			? ruleKeys.filter((ruleKey) => typeof ruleKey === 'string' && ruleKey.length > 0)
			: [];
	}

	buildTextPreview(value, maxLength = 120) {
		const text = String(value || '').replace(/\s+/g, ' ').trim();
		if (!Number.isInteger(maxLength) || maxLength <= 0) {
			return text;
		}

		return text.slice(0, maxLength);
	}

	reset() {
		this.rulesViewSelectorsByRule = {};
		this.rulesViewRuleMetadataByKey = {};
		this.baseRuleHasHighlightCandidatesByKey = {};
		this.ruleHasHighlightCandidatesByKey = {};
		this.activeRuleHighlightKeys = new Set();
		this.areRulesHighlightsVisible = false;
		window.clearTimeout(this.editorRehighlightTimerId);
		this.editorRehighlightTimerId = 0;
		this.editorRehighlightAttempts = 0;
		for (const documentNode of this.liveEditorPopupBindings.keys()) {
			this.teardownLiveEditorHoverPopupBinding(documentNode);
		}
		this.liveEditorPopupBindings.clear();
		this.setRulesToggleButtonsState({}, new Set());
	}

	highlightRulesViewSelectors(selectors, shouldScroll = true) {
		const rulesPreviewIframe = this.getRulesPreviewIframe();

		if (!rulesPreviewIframe || !Array.isArray(selectors) || selectors.length === 0) {
			this.areRulesHighlightsVisible = false;
			return 0;
		}

		const documentNode = rulesPreviewIframe.contentDocument;

		if (!documentNode) {
			return 0;
		}

		this.ensureRulesHighlightStyle(documentNode);

		const highlightedNodes = new Set();

		for (const rawSelector of selectors) {
			if (typeof rawSelector !== 'string' || rawSelector.length === 0) {
				continue;
			}

			let selectedNodes = [];

			try {
				selectedNodes = Array.from(documentNode.querySelectorAll(rawSelector));
			} catch {
				selectedNodes = [];
			}

			for (const selectedNode of selectedNodes) {
				if (!this.isNodeAllowedForHighlight(selectedNode)) {
					continue;
				}

				selectedNode.classList.add('a11yfix-rule-fail-highlight');
				highlightedNodes.add(selectedNode);
			}
		}

		const [firstHighlightedNode] = highlightedNodes;

		if (shouldScroll && firstHighlightedNode && typeof firstHighlightedNode.scrollIntoView === 'function') {
			firstHighlightedNode.scrollIntoView({ block: 'center', behavior: 'smooth' });
		}

		this.areRulesHighlightsVisible = highlightedNodes.size > 0;

		return highlightedNodes.size;
	}

	setRulesViewSelectorsByRule(failedSelectorsByRule, page = null) {
		const normalizedSelectorsByRule = {};
		const ruleMetadataByKey = {};
		const ruleHasHighlightCandidatesByKey = {};
		const activeRuleKeys = new Set();
		const knownRuleKeys = new Set([
			...Object.keys(failedSelectorsByRule || {}),
			...Object.keys(page?.rules || {}),
			...Object.keys(page?.ruleDetails || {}),
			...Object.keys(page?.rulePartsByRule || {})
		]);

		for (const ruleToggleButton of this.ruleToggleButtons) {
			const toggleKey = ruleToggleButton.dataset.ruleToggle || '';
			for (const mappedKey of this.resolveToggleRuleKeys(toggleKey)) {
				knownRuleKeys.add(mappedKey);
			}
		}

		for (const ruleKey of knownRuleKeys) {
			if (!ruleKey || ruleKey === 'ariaAndNames') {
				continue;
			}

			const rawSelectors = failedSelectorsByRule?.[ruleKey];
			const selectors = Array.isArray(rawSelectors)
				? Array.from(new Set(rawSelectors.filter((selector) => typeof selector === 'string' && selector.length > 0)))
				: [];
			const selectorParts = page?.rulePartsByRule?.[ruleKey]?.selectorParts || {};
			const details = page?.ruleDetails?.[ruleKey] || null;

			normalizedSelectorsByRule[ruleKey] = selectors;
			ruleMetadataByKey[ruleKey] = {
				selectorParts: selectorParts && typeof selectorParts === 'object' ? selectorParts : {},
				details
			};

			const hasHighlightCandidates = selectors.length > 0 || this.hasRuleFallbackCandidates(ruleKey, details, selectorParts);
			ruleHasHighlightCandidatesByKey[ruleKey] = hasHighlightCandidates;

			if (hasHighlightCandidates) {
				activeRuleKeys.add(ruleKey);
			}
		}

		this.rulesViewSelectorsByRule = normalizedSelectorsByRule;
		this.rulesViewRuleMetadataByKey = ruleMetadataByKey;
		this.baseRuleHasHighlightCandidatesByKey = { ...ruleHasHighlightCandidatesByKey };
		this.ruleHasHighlightCandidatesByKey = ruleHasHighlightCandidatesByKey;
		this.activeRuleHighlightKeys = activeRuleKeys;
		this.setRulesToggleButtonsState(this.rulesViewSelectorsByRule, this.activeRuleHighlightKeys);
	}

	refreshRuleAvailabilityForCurrentContext(targetDocuments, isEditorContext) {
		if (!isEditorContext) {
			this.ruleHasHighlightCandidatesByKey = { ...this.baseRuleHasHighlightCandidatesByKey };
			return;
		}

		const updatedAvailability = {};

		for (const ruleKey of Object.keys(this.rulesViewSelectorsByRule || {})) {
			updatedAvailability[ruleKey] = this.hasRuleEditorHighlightCandidates(ruleKey);
		}

		this.ruleHasHighlightCandidatesByKey = updatedAvailability;

		for (const activeRuleKey of Array.from(this.activeRuleHighlightKeys)) {
			if (updatedAvailability[activeRuleKey] !== true) {
				this.activeRuleHighlightKeys.delete(activeRuleKey);
			}
		}
	}

	hasRuleEditorHighlightCandidates(ruleKey) {
		if (typeof ruleKey !== 'string' || ruleKey.length === 0) {
			return false;
		}

		const selectors = this.getRuleSelectorsForDocument(ruleKey, true);

		if (Array.isArray(selectors) && selectors.length > 0) {
			return true;
		}

		const details = this.rulesViewRuleMetadataByKey?.[ruleKey]?.details;
		const selectorParts = this.rulesViewRuleMetadataByKey?.[ruleKey]?.selectorParts || {};

		return this.hasRuleFallbackCandidates(ruleKey, details, selectorParts);
	}

	getRuleVisibleNodeCountForContext(ruleKey, targetDocuments, isEditorContext = false) {
		if (!Array.isArray(targetDocuments) || targetDocuments.length === 0) {
			return 0;
		}

		const visibleNodes = new Set();
		const ruleSelectors = this.getRuleSelectorsForDocument(ruleKey, isEditorContext);

		for (const targetDocument of targetDocuments) {
			for (const rawSelector of ruleSelectors) {
				if (typeof rawSelector !== 'string' || rawSelector.length === 0) {
					continue;
				}

				let selectedNodes = [];

				try {
					selectedNodes = Array.from(targetDocument.querySelectorAll(rawSelector));
				} catch {
					selectedNodes = [];
				}

				for (const selectedNode of selectedNodes) {
					if (isEditorContext && !this.isNodeEligibleForEditorHighlight(selectedNode)) {
						continue;
					}

					visibleNodes.add(selectedNode);
				}
			}

			const fallbackNodes = this.findFallbackNodesForRule(ruleKey, targetDocument, isEditorContext);

			for (const fallbackNode of fallbackNodes) {
				if (!fallbackNode || !fallbackNode.classList) {
					continue;
				}

				visibleNodes.add(fallbackNode);
			}
		}

		return visibleNodes.size;
	}

	setRulesToggleButtonsState(selectorsByRule, activeRuleKeys) {
		for (const ruleToggleButton of this.ruleToggleButtons) {
			const toggleKey = ruleToggleButton.dataset.ruleToggle || '';
			const mappedRuleKeys = this.resolveToggleRuleKeys(toggleKey);

			if (mappedRuleKeys.length === 0) {
				continue;
			}

			const hasSelectors = mappedRuleKeys.some((ruleKey) => {
				const hasRuleAvailability = this.ruleHasHighlightCandidatesByKey?.[ruleKey];
				return typeof hasRuleAvailability === 'boolean'
					? hasRuleAvailability
					: (Array.isArray(selectorsByRule?.[ruleKey]) && selectorsByRule[ruleKey].length > 0);
			});
			const isActive = mappedRuleKeys.some((ruleKey) => activeRuleKeys.has(ruleKey));

			ruleToggleButton.disabled = !hasSelectors;
			ruleToggleButton.classList.toggle('is-active', hasSelectors && isActive);
			ruleToggleButton.setAttribute('aria-pressed', hasSelectors && isActive ? 'true' : 'false');
		}
	}

	restoreRulesViewToggleButtonsState() {
		this.setRulesToggleButtonsState(this.rulesViewSelectorsByRule, this.activeRuleHighlightKeys);
	}

	disableRulesViewToggleButtons() {
		for (const ruleToggleButton of this.ruleToggleButtons) {
			ruleToggleButton.disabled = true;
			ruleToggleButton.classList.remove('is-active');
			ruleToggleButton.setAttribute('aria-pressed', 'false');
		}
	}

	applyRulesHighlightsFromActiveRules(shouldScroll = true) {
		this.clearRulesViewHighlights();
		const rulesPreviewIframe = this.getRulesPreviewIframe();

		if (!rulesPreviewIframe || !rulesPreviewIframe.contentDocument) {
			return 0;
		}

		const documentNode = rulesPreviewIframe.contentDocument;
		const isEditorContext = this.isEditorDocument(documentNode) || this.hasEditorCanvasIframe(documentNode);
		const targetDocuments = this.getHighlightTargetDocuments(documentNode, isEditorContext);

		if (isEditorContext) {
			const latestFrontendSnapshot = this.getLatestRulesViewMapSnapshotByMode('frontend');

			if (latestFrontendSnapshot?.map) {
				const mappedHighlightResult = this.mapLiveBlocksToEditorAndHighlight(latestFrontendSnapshot.map);
				const highlightedCount = Number.isInteger(mappedHighlightResult?.highlightedMatchedCount)
					? mappedHighlightResult.highlightedMatchedCount
					: 0;

				this.areRulesHighlightsVisible = highlightedCount > 0;

				if (shouldScroll && highlightedCount === 0 && targetDocuments.length === 1) {
					this.scheduleEditorRehighlightAttempt();
				}

				return highlightedCount;
			}
		}

		this.refreshRuleAvailabilityForCurrentContext(targetDocuments, isEditorContext);
		this.setRulesToggleButtonsState(this.rulesViewSelectorsByRule, this.activeRuleHighlightKeys);

		for (const targetDocument of targetDocuments) {
			this.ensureRulesHighlightStyle(targetDocument);
			this.ensureLiveEditorHoverPopupBinding(targetDocument);
		}

		const highlightedNodes = new Set();
		let shouldScrollToFirst = shouldScroll;

		for (const ruleKey of this.activeRuleHighlightKeys) {
			const ruleSelectors = this.getRuleSelectorsForDocument(ruleKey, isEditorContext);
			let selectorHits = 0;
			const selectorHitsByDocument = [];
			const fallbackHitsByDocument = [];

			for (const [index, targetDocument] of targetDocuments.entries()) {
				const hitsInDocument = this.highlightSelectorsInDocument(targetDocument, ruleSelectors, highlightedNodes, ruleKey, isEditorContext);
				selectorHits += hitsInDocument;
				selectorHitsByDocument.push({
					documentIndex: index,
					hits: hitsInDocument
				});
			}

			if (selectorHits > 0) {
				continue;
			}

			let fallbackHits = 0;

			for (const [index, targetDocument] of targetDocuments.entries()) {
				const fallbackNodes = this.findFallbackNodesForRule(ruleKey, targetDocument, isEditorContext);
				let hitsInDocument = 0;

				for (const fallbackNode of fallbackNodes) {
					if (!fallbackNode || !fallbackNode.classList) {
						continue;
					}

					if (!this.isNodeAllowedForHighlight(fallbackNode)) {
						continue;
					}

					fallbackNode.classList.add('a11yfix-rule-fail-highlight');
					this.appendRuleHoverDataForNode(fallbackNode, ruleKey);
					highlightedNodes.add(fallbackNode);
					hitsInDocument += 1;
					fallbackHits += 1;
				}

				fallbackHitsByDocument.push({
					documentIndex: index,
					hits: hitsInDocument
				});
			}

		}

		const firstHighlightedNode = highlightedNodes.values().next().value;

		if (shouldScrollToFirst && firstHighlightedNode && typeof firstHighlightedNode.scrollIntoView === 'function') {
			firstHighlightedNode.scrollIntoView({ block: 'center', behavior: 'smooth' });
			shouldScrollToFirst = false;
		}

		this.areRulesHighlightsVisible = highlightedNodes.size > 0;

		if (isEditorContext && highlightedNodes.size === 0 && targetDocuments.length === 1) {
			this.scheduleEditorRehighlightAttempt();
		} else {
			window.clearTimeout(this.editorRehighlightTimerId);
			this.editorRehighlightTimerId = 0;
			this.editorRehighlightAttempts = 0;
		}
		return highlightedNodes.size;
	}

	recordRulesViewMapSnapshot(snapshot) {
		if (!snapshot || typeof snapshot !== 'object') {
			return;
		}

		this.rulesViewMapHistory.push(snapshot);
		if (this.rulesViewMapHistory.length > 40) {
			this.rulesViewMapHistory = this.rulesViewMapHistory.slice(-40);
		}
	}

	getLatestRulesViewMapSnapshotByMode(mode) {
		if (!Array.isArray(this.rulesViewMapHistory) || typeof mode !== 'string' || mode.length === 0) {
			return null;
		}

		for (let index = this.rulesViewMapHistory.length - 1; index >= 0; index -= 1) {
			const snapshot = this.rulesViewMapHistory[index];

			if (snapshot?.mode === mode) {
				return snapshot;
			}
		}

		return null;
	}

	buildRulesViewWpBlocksMap() {
		return inspectionBuildRulesViewWpBlocksMap(this);
	}

	collectFailedRuleKeysByBlockNode(documentNode) {
		return inspectionCollectFailedRuleKeysByBlockNode(this, documentNode);
	}

	createBlockRegionContext(documentNode) {
		return inspectionCreateBlockRegionContext(this, documentNode);
	}

	getBlockRegionHeaderSelectors() {
		return inspectionGetBlockRegionHeaderSelectors(this);
	}

	getBlockRegionFooterSelectors() {
		return inspectionGetBlockRegionFooterSelectors(this);
	}

	collectBlockRegionContainers(documentNode, selectors) {
		return inspectionCollectBlockRegionContainers(this, documentNode, selectors);
	}

	classifyBlockRegion(node, regionContext) {
		return inspectionClassifyBlockRegion(this, node, regionContext);
	}

	isNodeWithinRegionContainers(node, containers) {
		return inspectionIsNodeWithinRegionContainers(this, node, containers);
	}

	hasNodeAncestorToken(node, tokens) {
		return inspectionHasNodeAncestorToken(this, node, tokens);
	}


	collectEditorCanvasDocuments(rootDocument) {
		return inspectionCollectEditorCanvasDocuments(this, rootDocument);
	}

	flattenBlocksByType(blocksByType) {
		return matchingFlattenBlocksByType(this, blocksByType);
	}

	buildEditorCanvasBlocksMap() {
		return matchingBuildEditorCanvasBlocksMap(this);
	}

	canonicalizeBlockType(rawBlockType) {
		return matchingCanonicalizeBlockType(this, rawBlockType);
	}

	scoreLiveToEditorBlockMatch(liveBlock, editorBlock) {
		return matchingScoreLiveToEditorBlockMatch(this, liveBlock, editorBlock);
	}

	resolveFirstPostContentIndex(liveBlocks) {
		return matchingResolveFirstPostContentIndex(this, liveBlocks);
	}

	shouldIncludeLiveBlockForEditorMapping(liveBlock, firstPostContentIndex) {
		return matchingShouldIncludeLiveBlockForEditorMapping(this, liveBlock, firstPostContentIndex);
	}

	resolveBlockMatchThreshold(liveBlock) {
		return matchingResolveBlockMatchThreshold(this, liveBlock);
	}

	shouldHighlightMappedEditorBlock(liveBlock) {
		return matchingShouldHighlightMappedEditorBlock(this, liveBlock);
	}

	mapLiveBlocksToEditorAndHighlight(liveMap) {
		return matchingMapLiveBlocksToEditorAndHighlight(this, liveMap);
	}

	clearLiveToEditorMatchHighlights() {
		return matchingClearLiveToEditorMatchHighlights(this);
	}

	generateA11yFixUuid() {
		return matchingGenerateA11yFixUuid(this);
	}

	getLiveEditorMatchSelectorForNode(node) {
		return matchingGetLiveEditorMatchSelectorForNode(this, node);
	}

	buildLiveEditorMatchHoverText(liveBlock) {
		return popupBuildLiveEditorMatchHoverText(this, liveBlock);
	}

	buildRuleSnippetForHover(ruleKey, liveBlock) {
		return popupBuildRuleSnippetForHover(this, ruleKey, liveBlock);
	}

	setLiveEditorMatchHoverData(node, liveBlock) {
		popupSetLiveEditorMatchHoverData(this, node, liveBlock);
	}

	resolveBlockTypeFromNode(node) {
		return popupResolveBlockTypeFromNode(node);
	}

	appendRuleHoverDataForNode(node, ruleKey) {
		popupAppendRuleHoverDataForNode(this, node, ruleKey);
	}

	ensureLiveEditorHoverPopupBinding(documentNode) {
		popupEnsureLiveEditorHoverPopupBinding(this, documentNode);
	}

	teardownLiveEditorHoverPopupBinding(documentNode) {
		popupTeardownLiveEditorHoverPopupBinding(this, documentNode);
	}

	ensureLiveEditorHoverPopupNode(documentNode) {
		return popupEnsureLiveEditorHoverPopupNode(documentNode);
	}

	showLiveEditorHoverPopup(documentNode, popupText) {
		popupShowLiveEditorHoverPopup(this, documentNode, popupText);
	}

	hideLiveEditorHoverPopup(documentNode) {
		popupHideLiveEditorHoverPopup(documentNode);
	}

	setLiveToEditorMatchStyle(documentNode, selectors) {
		matchingSetLiveToEditorMatchStyle(this, documentNode, selectors);
	}

	scheduleEditorRehighlightAttempt() {
		if (this.editorRehighlightAttempts >= 4 || this.editorRehighlightTimerId) {
			return false;
		}

		this.editorRehighlightAttempts += 1;
		this.editorRehighlightTimerId = window.setTimeout(() => {
			this.editorRehighlightTimerId = 0;
			this.applyRulesHighlightsFromActiveRules(false);
		}, 700);

		return true;
	}

	describeTargetDocument(documentNode, index) {
		return inspectionDescribeTargetDocument(this, documentNode, index);
	}

	getRuleSelectorsForDocument(ruleKey, isEditorContext) {
		const selectors = this.rulesViewSelectorsByRule?.[ruleKey] || [];

		if (!isEditorContext) {
			return selectors;
		}

		return selectors.filter((selector) => this.isPagePartSelector(ruleKey, selector) && this.isEditorSafeSelector(selector));
	}

	highlightSelectorsInDocument(documentNode, selectors, highlightedNodes, ruleKey = '', isEditorContext = false) {
		let hits = 0;

		for (const rawSelector of selectors) {
			if (typeof rawSelector !== 'string' || rawSelector.length === 0) {
				continue;
			}

			let selectedNodes = [];

			try {
				selectedNodes = Array.from(documentNode.querySelectorAll(rawSelector));
			} catch {
				selectedNodes = [];
			}

			for (const selectedNode of selectedNodes) {
				if (!this.isNodeAllowedForHighlight(selectedNode)) {
					continue;
				}

				if (isEditorContext && !this.isNodeEligibleForEditorHighlight(selectedNode)) {
					continue;
				}

				selectedNode.classList.add('a11yfix-rule-fail-highlight');
				if (ruleKey && typeof ruleKey === 'string') {
					this.appendRuleHoverDataForNode(selectedNode, ruleKey);
				}
				highlightedNodes.add(selectedNode);
				hits += 1;
			}
		}

		return hits;
	}

	findRuleKeyBySelector(targetSelector) {
		if (typeof targetSelector !== 'string' || targetSelector.length === 0) {
			return '';
		}

		for (const [ruleKey, selectors] of Object.entries(this.rulesViewSelectorsByRule || {})) {
			if (!Array.isArray(selectors)) {
				continue;
			}

			if (selectors.includes(targetSelector)) {
				return ruleKey;
			}
		}

		return '';
	}

	findFallbackNodesForRule(ruleKey, documentNode, isEditorContext) {
		return fallbackFindFallbackNodesForRule(this, ruleKey, documentNode, isEditorContext);
	}

	hasEditorCanvasIframe(documentNode) {
		return inspectionHasEditorCanvasIframe(this, documentNode);
	}

	getHighlightTargetDocuments(rootDocument, includeNestedIframes = false) {
		return inspectionGetHighlightTargetDocuments(this, rootDocument, includeNestedIframes);
	}

	findFallbackLinkNodes(details, documentNode) {
		return fallbackFindFallbackLinkNodes(this, details, documentNode);
	}

	findFallbackHeadingNodes(details, documentNode) {
		return fallbackFindFallbackHeadingNodes(this, details, documentNode);
	}

	findFallbackImageNodes(details, documentNode) {
		return fallbackFindFallbackImageNodes(this, details, documentNode);
	}

	findFallbackFormControlNodes(details, documentNode) {
		return fallbackFindFallbackFormControlNodes(this, details, documentNode);
	}

	findFallbackKeyboardNodes(details, documentNode) {
		return fallbackFindFallbackKeyboardNodes(this, details, documentNode);
	}

	findFallbackLowContrastNodes(details, documentNode) {
		return fallbackFindFallbackLowContrastNodes(this, details, documentNode);
	}

	getLowContrastIssueItems(details) {
		return fallbackGetLowContrastIssueItems(this, details);
	}

	extractSelectorTokens(selector) {
		return fallbackExtractSelectorTokens(this, selector);
	}

	matchesSelectorTokenHints(node, tokens) {
		return fallbackMatchesSelectorTokenHints(this, node, tokens);
	}

	getRuleFallbackDiagnostics(ruleKey) {
		return fallbackGetRuleFallbackDiagnostics(this, ruleKey);
	}

	findNodesBySelectorTail(selector, documentNode) {
		return fallbackFindNodesBySelectorTail(this, selector, documentNode);
	}

	hasStrongTokenOverlap(targetText, candidateText, minimumMatches = 3) {
		return fallbackHasStrongTokenOverlap(this, targetText, candidateText, minimumMatches);
	}

	isEditorSafeSelector(selector) {
		if (typeof selector !== 'string' || selector.length === 0) {
			return true;
		}

		const normalized = selector.toLowerCase();
		const blockedFragments = [
			'#wpadminbar',
			'#wp-admin-bar-',
			'#wp-skip-link',
			'#adminmenu',
			'#screen-meta',
			'#wpfooter'
		];

		return !blockedFragments.some((fragment) => normalized.includes(fragment));
	}

	isNodeEligibleForEditorHighlight(node) {
		if (!node || node.nodeType !== 1 || typeof node.closest !== 'function') {
			return false;
		}

		if (!this.isNodeAllowedForHighlight(node)) {
			return false;
		}

		if (node.closest('#wpadminbar, #adminmenumain, #adminmenuwrap, #screen-meta, #wpfooter, #wpbody-content > .wrap')) {
			return false;
		}

		return true;
	}

	isNodeAllowedForHighlight(node) {
		if (!node || node.nodeType !== 1 || typeof node.closest !== 'function') {
			return false;
		}

		return !node.closest('#wpadminbar');
	}

	filterEditorEligibleNodes(nodes) {
		if (!Array.isArray(nodes)) {
			return [];
		}

		return nodes.filter((node) => this.isNodeEligibleForEditorHighlight(node));
	}

	isEditorDocument(documentNode) {
		if (!documentNode) {
			return false;
		}

		const body = documentNode.body;
		const pathname = documentNode.location?.pathname || '';

		if (body?.classList?.contains('wp-admin')) {
			return true;
		}

		return pathname.includes('/wp-admin/');
	}

	isPagePartSelector(ruleKey, selector) {
		if (typeof selector !== 'string' || selector.length === 0) {
			return true;
		}

		const selectorParts = this.rulesViewRuleMetadataByKey?.[ruleKey]?.selectorParts || {};
		const labels = selectorParts?.[selector];

		if (!Array.isArray(labels) || labels.length === 0) {
			return true;
		}

		return labels.includes('page');
	}

	hasRuleFallbackCandidates(ruleKey, details, selectorParts) {
		if (!details || typeof details !== 'object') {
			return false;
		}

		const hasPageSelector = (selector) => {
			if (typeof selector !== 'string' || selector.length === 0) {
				return true;
			}

			const labels = selectorParts?.[selector];

			if (!Array.isArray(labels) || labels.length === 0) {
				return true;
			}

			return labels.includes('page');
		};

		if (ruleKey === 'linkTitle') {
			return Array.isArray(details.invalidLinks) && details.invalidLinks.some((item) => hasPageSelector(item?.selector));
		}

		if (ruleKey === 'headingOrder') {
			return Array.isArray(details.failedItems) && details.failedItems.some((item) => hasPageSelector(item?.selector));
		}

		if (ruleKey === 'imageAlt') {
			return Array.isArray(details.invalidImages) && details.invalidImages.some((item) => hasPageSelector(item?.selector));
		}

		if (ruleKey === 'formLabels') {
			return Array.isArray(details.invalidControls) && details.invalidControls.some((item) => hasPageSelector(item?.selector));
		}

		if (ruleKey === 'keyboardFocusable') {
			return Array.isArray(details.issues) && details.issues.some((item) => hasPageSelector(item?.selector));
		}

		if (ruleKey === 'accessibleNamePreferredSource') {
			return Array.isArray(details.issues) && details.issues.some((item) => hasPageSelector(item?.selector));
		}

		if (ruleKey === 'ariaLabelledbyIntegrity') {
			return Array.isArray(details.issues) && details.issues.some((item) => hasPageSelector(item?.selector));
		}

		if (ruleKey === 'ariaHiddenInteractive') {
			return Array.isArray(details.issues) && details.issues.some((item) => hasPageSelector(item?.selector));
		}

		if (ruleKey === 'lowColorContrast') {
			return Array.isArray(details.issues) && details.issues.some((item) => hasPageSelector(item?.selector));
		}

		return false;
	}

	normalizeComparableText(value) {
		return String(value || '')
			.replace(/\s+/g, ' ')
			.trim()
			.toLowerCase();
	}

	normalizeCompactComparableText(value) {
		return String(value || '')
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, '');
	}

	normalizeComparableUrl(rawUrl, documentNode) {
		const input = String(rawUrl || '').trim();

		if (!input) {
			return '';
		}

		try {
			const baseUrl = documentNode?.location?.href || window.location.href;
			const parsed = new URL(input, baseUrl);
			parsed.hash = '';
			return parsed.toString();
		} catch {
			return input;
		}
	}

	escapeSelectorValue(value) {
		if (window.CSS && typeof window.CSS.escape === 'function') {
			return window.CSS.escape(String(value));
		}

		return String(value).replace(/([#.;,[\]()>+~*:=!"'\\])/g, '\\$1');
	}

	clearRulesViewHighlights() {
		const rulesPreviewIframe = this.getRulesPreviewIframe();

		if (!rulesPreviewIframe || !rulesPreviewIframe.contentDocument) {
			return 0;
		}

		const rootDocument = rulesPreviewIframe.contentDocument;
		const isEditorContext = this.isEditorDocument(rootDocument) || this.hasEditorCanvasIframe(rootDocument);
		const targetDocuments = this.getHighlightTargetDocuments(rootDocument, isEditorContext);
		let clearedCount = 0;

		for (const targetDocument of targetDocuments) {
			this.hideLiveEditorHoverPopup(targetDocument);
			const highlightedNodes = targetDocument.querySelectorAll('.a11yfix-rule-fail-highlight');

			for (const node of highlightedNodes) {
				node.classList.remove('a11yfix-rule-fail-highlight');
				node.removeAttribute('data-a11yfix-rule-popup');
				node.removeAttribute('data-a11yfix-rule-popup-json');
				node.removeAttribute('data-a11yfix-rule-keys');
				clearedCount += 1;
			}
		}

		const adminBarHighlightedNodes = rootDocument.querySelectorAll('#wpadminbar .a11yfix-rule-fail-highlight');

		for (const node of adminBarHighlightedNodes) {
			node.classList.remove('a11yfix-rule-fail-highlight');
			node.removeAttribute('data-a11yfix-rule-popup');
			node.removeAttribute('data-a11yfix-rule-popup-json');
			node.removeAttribute('data-a11yfix-rule-keys');
			clearedCount += 1;
		}

		this.areRulesHighlightsVisible = false;
		return clearedCount;
	}

	toggleRulesViewRule(ruleKey) {
		const rulesPreviewIframe = this.getRulesPreviewIframe();

		if (!rulesPreviewIframe || !rulesPreviewIframe.contentDocument) {
			return {
				ready: false,
				ruleKey,
				hasSelectors: false,
				isActive: false,
				highlightedCount: 0
			};
		}

		const targetRuleKeys = this.resolveToggleRuleKeys(ruleKey);
		const availableRuleKeys = targetRuleKeys.filter((key) => this.ruleHasHighlightCandidatesByKey?.[key] === true);
		const hasHighlightCandidates = availableRuleKeys.length > 0;

		if (!hasHighlightCandidates) {
			return {
				ready: true,
				ruleKey,
				hasSelectors: false,
				isActive: false,
				highlightedCount: 0
			};
		}

		const isAnyTargetActive = availableRuleKeys.some((key) => this.activeRuleHighlightKeys.has(key));

		if (isAnyTargetActive) {
			for (const key of availableRuleKeys) {
				this.activeRuleHighlightKeys.delete(key);
			}
		} else {
			for (const key of availableRuleKeys) {
				this.activeRuleHighlightKeys.add(key);
			}
		}

		const highlightedCount = this.applyRulesHighlightsFromActiveRules(false);
		const isActive = availableRuleKeys.some((key) => this.activeRuleHighlightKeys.has(key));
		this.setRulesToggleButtonsState(this.rulesViewSelectorsByRule, this.activeRuleHighlightKeys);

		return {
			ready: true,
			ruleKey,
			hasSelectors: true,
			isActive,
			highlightedCount
		};
	}

	ensureRulesHighlightStyle(documentNode) {
		if (!documentNode || documentNode.getElementById('a11yfix-rule-highlight-style')) {
			return;
		}

		const styleNode = documentNode.createElement('style');
		styleNode.id = 'a11yfix-rule-highlight-style';
		styleNode.textContent = '.a11yfix-rule-fail-highlight{outline:2px solid #d63638 !important;outline-offset:2px !important;background-color:rgba(214,54,56,0.12) !important;}.a11yfix-rule-fail-highlight:is(img,svg,video,canvas,picture){outline-offset:-2px !important;}';
		documentNode.head.appendChild(styleNode);
	}
}
